import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Bot, MessageSquare, MessageCircle, Shield, Activity, BarChart3, Zap, Globe, Package,
  ChevronDown, ChevronRight, DollarSign, Layers, Send, TrendingUp,
  Check, ArrowRight, Sparkles, Star, Eye, Code2, Store, Users, Chrome,
  Lock, Brain, Cpu, Terminal, Rocket, Target, LineChart, Heart,
  Search, ShoppingCart, Truck, Megaphone, ArrowUpRight, Play,
  CreditCard, Percent, Gauge, Network, Mail, Phone, X, Menu, Wallet,
  Wifi, BatteryCharging, Home
} from 'lucide-react';
import { useCurrency, CurrencyFlag } from '../contexts/CurrencyContext';
import { CILogo } from '../components/CILogo';
import { LINKS } from '../config/siteLinks';
import PhoneStartModal from '../components/PhoneStartModal';
import ShopifyAppStoreCTA from '../components/ShopifyAppStoreCTA';
import QRCodeStart from '../components/QRCodeStart';
import { SEOHelmet } from '../components/SEOHelmet';

const WHATSAPP_SIGNUP_NUMBER = '+16094693740';

// ─── Animated typing terminal ─────────────────────────────────
function AnimatedTerminal() {
  const [lines, setLines] = useState([]);
  const [currentLine, setCurrentLine] = useState(0);

  const script = [
    { type: 'input', text: '> /pulse' },
    { type: 'output', text: '⚡ Revenue: $14,230 (+12% vs yesterday)' },
    { type: 'output', text: '📦 Orders: 89 today (3 exceptions flagged)' },
    { type: 'output', text: '🤖 Agents: 5 active, 23 decisions today' },
    { type: 'output', text: '🏥 Health Score: 847/1000' },
    { type: 'input', text: '> /margins' },
    { type: 'output', text: '✅ Promise Integrity: 94.2%' },
    { type: 'output', text: '📊 PCR: 2.1% | Backorder Risk: Low' },
    { type: 'output', text: '💰 Avg Margin: 34.2% (target: 30%)' },
    { type: 'input', text: '> /agents status' },
    { type: 'output', text: '🟢 Pricing Agent — trusted · 142 decisions' },
    { type: 'output', text: '🟢 Deal Agent — supervised · 67 decisions' },
    { type: 'output', text: '🟢 Inventory Agent — autonomous · 891 decisions' },
  ];

  useEffect(() => {
    if (currentLine >= script.length) {
      const timer = setTimeout(() => {
        setLines([]);
        setCurrentLine(0);
      }, 4000);
      return () => clearTimeout(timer);
    }
    const delay = script[currentLine].type === 'input' ? 1200 : 400;
    const timer = setTimeout(() => {
      setLines(prev => [...prev, script[currentLine]]);
      setCurrentLine(prev => prev + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [currentLine]);

  return (
    <div className="bg-[#0D1628] rounded-xl border border-[#22304A] overflow-hidden shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#22304A] bg-[#0B1120]">
        <div className="w-3 h-3 rounded-full bg-red-500/80" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
        <div className="w-3 h-3 rounded-full bg-green-500/80" />
        <span className="ml-2 text-xs text-[#64748B] font-mono">OpenClaw — Telegram</span>
      </div>
      <div className="p-4 font-mono text-sm min-h-[280px]">
        {lines.map((line, i) => (
          <div key={i} className={`mb-1 ${line.type === 'input' ? 'text-[#3EF1C7] font-semibold mt-3' : 'text-[#94A3B8]'}`}>
            {line.text}
          </div>
        ))}
        <span className="inline-block w-2 h-4 bg-[#3EF1C7] animate-pulse" />
      </div>
    </div>
  );
}

// ─── Score ring visualization ────────────────────────────────
function ScoreRing({ score, label, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 1000) * circumference;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#22304A" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#3EF1C7" strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-2xl font-bold text-white">{score}</span>
        <span className="text-xs text-[#64748B]">/1000</span>
      </div>
      {label && <span className="mt-2 text-sm text-[#94A3B8]">{label}</span>}
    </div>
  );
}

// ─── Live network counter (Money Found across all merchants) ──
function NetworkCounter() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/growth/network-stats')
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled && data) setStats(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mb-8 grid grid-cols-2 gap-3 max-w-xl">
      <div className="text-center p-3 rounded-lg bg-[#0D1628] border border-[#22304A]">
        <div className="text-xl font-bold text-[#3EF1C7]">
          {stats && stats.total_money_found_usd > 0
            ? `$${Math.round(stats.total_money_found_usd).toLocaleString()}`
            : '43 specialist agents'}
        </div>
        <div className="text-xs text-[#64748B] mt-1">
          {stats && stats.total_money_found_usd > 0
            ? 'found for merchants so far'
            : 'scanning pricing, inventory, deals & fulfillment'}
        </div>
      </div>
      <div className="text-center p-3 rounded-lg bg-[#0D1628] border border-[#22304A]">
        <div className="text-xl font-bold text-[#3EF1C7]">
          {stats && stats.queue ? `#${(stats.queue.waiting + 1).toLocaleString()}` : 'Limited'}
        </div>
        <div className="text-xs text-[#64748B] mt-1">
          {stats && stats.queue
            ? 'next spot in the free onboarding queue'
            : 'free onboarding slots each week'}
        </div>
      </div>
    </div>
  );
}

// ─── Main LandingPage component ──────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeAgent, setActiveAgent] = useState(0);
  const [activeMsgPlatform, setActiveMsgPlatform] = useState('telegram');
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneModalContext, setPhoneModalContext] = useState({});
  const openPhoneModal = (context = {}) => { setPhoneModalContext(context); setPhoneModalOpen(true); };
  const { currency, symbol } = useCurrency();

  // Multi-currency pricing
  const CURRENCY_PRICES = {
    usd: { pro: 49, scale: 199 },
    cad: { pro: 65, scale: 269 },
    inr: { pro: 3999, scale: 14999 },
  };
  const curPrices = CURRENCY_PRICES[currency] || CURRENCY_PRICES.usd;

  const getPrice = (monthly) => {
    if (billingCycle === 'annual') return Math.round(monthly * 0.8);
    return monthly;
  };

  const fmtPrice = (amount) => {
    if (amount === null || amount === undefined) return 'Custom';
    if (currency === 'inr') return `${symbol}${amount.toLocaleString('en-IN')}`;
    return `${symbol}${amount.toLocaleString()}`;
  };

  // ── Data ──
  const agents = [
    { name: 'Pricing Agent', icon: DollarSign, desc: 'Monitors competitor prices, analyzes elasticity, and recommends optimal pricing in real-time.', tier: 'trusted', decisions: 142 },
    { name: 'Deal Agent', icon: Percent, desc: 'Creates, manages, and optimizes deals and promotions. Publishes to thedeals.ai automatically.', tier: 'supervised', decisions: 67 },
    { name: 'Inventory Agent', icon: Package, desc: 'Predicts stockouts, calculates reorder points, and manages inventory levels across channels.', tier: 'autonomous', decisions: 891 },
    { name: 'Fulfillment Agent', icon: Truck, desc: 'Tracks shipments, detects exceptions, manages carrier selection, and resolves delivery issues.', tier: 'supervised', decisions: 54 },
    { name: 'CX Agent', icon: Heart, desc: 'Monitors customer sentiment, handles support escalations, and tracks NPS scores.', tier: 'probation', decisions: 12 },
    { name: 'SEO Agent', icon: Search, desc: 'Optimizes product listings, meta tags, and content for search engine visibility.', tier: 'probation', decisions: 8 },
    { name: 'Distribution Agent', icon: Megaphone, desc: 'Manages multi-channel distribution, social media AI, demand pools, and growth strategies.', tier: 'supervised', decisions: 45 },
  ];

  const trustTiers = [
    { name: 'Probation', color: '#F55454', desc: 'All actions need approval', threshold: '0 decisions' },
    { name: 'Supervised', color: '#FFD166', desc: 'Low-value auto-approved', threshold: '20+ decisions, 80%+ success' },
    { name: 'Trusted', color: '#3EF1C7', desc: 'Most actions auto-approved', threshold: '100+ decisions, 90%+ success' },
    { name: 'Autonomous', color: '#7A5CFF', desc: 'Full autonomy within guardrails', threshold: '500+ decisions, 95%+ success' },
  ];

  const flagshipEngines = [
    { name: 'Funding Engine', icon: CreditCard, color: '#3EF1C7', features: ['13-week cashflow', 'Multi-lender routing', 'Collateral assessment', 'Bond market analysis', 'Powered by 8 sub-engines'] },
    { name: 'Distribution Engine', icon: Globe, color: '#7A5CFF', features: ['Demand sensing', 'Content generation', 'Multi-channel growth', 'Virality loops', 'Powered by 4 sub-engines'] },
    { name: 'Feedback Engine', icon: MessageSquare, color: '#FFD166', features: ['Agentic NPS', 'Sentiment analysis', 'Voice-of-customer reports', 'Auto-action triggers'] },
    { name: 'Product Engine', icon: Rocket, color: '#F55454', features: ['Discovery agent', 'Launch orchestration', 'Revenue-stream analysis', 'Market-fit scoring'] },
  ];

  const foundationEngines = [
    { slug: 'health-scoring', name: 'Health & Scoring', icon: Activity, color: '#3EF1C7',
      subEngines: ['Commerce Health Score', 'CI Score v2', 'Guardrail Engine'] },
    { slug: 'memory-audit', name: 'Memory & Audit', icon: Brain, color: '#7A5CFF',
      subEngines: ['Memory Facade (5-tier)', 'Entity Resolver', 'Transcript Writer'] },
    { slug: 'intelligence', name: 'Intelligence & Discovery', icon: Sparkles, color: '#FFD166',
      subEngines: ['Prediction Engine', 'Opportunity Engine', 'Margin Guard', 'Context Graph V2'] },
    { slug: 'orchestration', name: 'Orchestration', icon: Network, color: '#F55454',
      subEngines: ['Playbook Engine', 'Flow Engine', 'Conversational Router'] },
    { slug: 'inventory-fulfillment', name: 'Inventory & Fulfillment', icon: Package, color: '#3EF1C7',
      subEngines: ['Allocation Engine', 'Sourcing Engine', 'Cubing Engine', 'Aging Pipeline', 'Order Orchestration'] },
    { slug: 'decision-graph', name: 'Decision Graph', icon: Layers, color: '#7A5CFF',
      subEngines: ['Context Graph V2', 'Policy Gate', 'Settlement Engine'] },
    { slug: 'marketplace-coordination', name: 'Marketplace Coordination', icon: Users, color: '#FFD166',
      subEngines: ['RFQ Matching', 'Reputation Scorer', 'T-shirt Cost Estimator'] },
    { slug: 'creator-analytics', name: 'Creator Analytics', icon: LineChart, color: '#F55454',
      subEngines: ['Cohort Retention', 'LTV', 'Attribution', 'Insight Generator'] },
    { slug: 'financial-health', name: 'Financial Health', icon: DollarSign, color: '#3EF1C7',
      subEngines: ['Customer Profitability', 'Product Profitability', 'MCA Protection', 'Extended Forecast'] },
  ];

  const pricingPlans = [
    {
      name: 'Free', price: 0, credits: '1,000', highlight: false,
      features: ['1,000 credits/month', '2 API keys', 'CI Proof widget', 'Basic health score', 'Community support', 'No outcome fees'],
      cta: 'Start Free',
    },
    {
      name: 'Pro', price: curPrices.pro, credits: '10,000', highlight: false,
      features: ['10,000 credits/month', '10 API keys', 'AI CEO + 43 specialist agents (84-role org design)', 'Claude AI Chat', 'Full health score', 'OpenClaw messaging', 'Trust ladder', '2% outcome fee', 'Email support'],
      cta: 'Start Pro',
    },
    {
      name: 'Scale', price: curPrices.scale, credits: '100,000', highlight: true,
      features: ['100,000 credits/month', '50 API keys', 'AI CEO + 43 specialist agents (84-role org design)', 'Claude AI Chat + Deep Review', 'Compute+ operations (preview)', 'Priority OpenClaw', 'Advanced guardrails', '1.5% outcome fee', 'Priority support', 'USDC payments'],
      cta: 'Start Scale',
    },
    {
      name: 'Enterprise', price: null, credits: '1,000,000+', highlight: false,
      features: ['Custom credit allocation', '500 API keys', 'Dedicated infrastructure', 'Custom agents', 'Mac Mini deployment', 'Local LLM (Ollama)', '1% outcome fee', 'Dedicated support', 'SLA guarantee'],
      cta: 'Contact Sales',
    },
  ];

  const faqItems = [
    {
      question: "What is Commerce Index?",
      answer: "Commerce Index is an agentic commerce operating system. One AI CEO (goal_manager) coordinates 43 specialist agents across 7 business functions (Sales & Marketing, Pricing & Deals, E-commerce & Fulfillment, Customer Service, Finance, Compliance & IP, Leadership & Learning) in an 84-role org design that grows as the platform does. You can manage everything from messaging apps like WhatsApp or iMessage."
    },
    {
      question: "How does the credit system work?",
      answer: "Every API call costs credits based on complexity: simple lookups cost 1 credit, enriched data costs 5, AI-powered operations cost 25-100. Your plan includes a monthly credit allocation. Need more? Buy additional credits anytime from the Subscription page — they never expire and roll over each month. 1 credit ≈ $0.008 USD."
    },
    {
      question: "What are outcome fees?",
      answer: "Outcome fees are our way of aligning with your success. When our agents create real commerce value — like completing a sale, recovering an abandoned cart, or optimizing a price — we take a small percentage (1-2% depending on plan). You only pay when we deliver results."
    },
    {
      question: "Do I need Shopify to use Commerce Index?",
      answer: "No. While we offer one-click Shopify integration, Commerce Index works with any platform via our REST API. You can also go messaging-only with OpenClaw — no dashboard needed."
    },
    {
      question: "What is the Trust Ladder?",
      answer: "Every agent starts on 'Probation' where all actions need your approval. As agents prove themselves with successful decisions, they automatically upgrade through Supervised → Trusted → Autonomous tiers, gaining more autonomy. You control the guardrails at every level."
    },
    {
      question: "Can I run this on my own infrastructure?",
      answer: "Yes. Our Enterprise plan supports Mac Mini deployment with Docker, MongoDB, Redis, and Ollama for local LLM inference. We provide a complete setup guide with Cloudflare Tunnel for secure remote access. Total cost: ~$25-55/month for infrastructure."
    },
    {
      question: "What messaging platforms are supported?",
      answer: "OpenClaw supports Telegram, WhatsApp Business, iMessage (via Mac Mini), Slack, and email. All conversations sync across platforms in real-time, so you can start a conversation on Telegram and continue on Slack."
    },
    {
      question: "How is my data protected?",
      answer: "We use 5-layer security: request validation, agent identity verification, rate limiting, behavioral analysis, and VPS isolation. All data is encrypted at rest and in transit. We enforce jurisdiction-aware legal compliance and maintain a prohibited goods list."
    },
    {
      question: "What is the Commerce Index ecosystem?",
      answer: "Three platforms work together: commerceindex.com (merchant OS), commerceindex.ai (agent marketplace + AI infrastructure), and thedeals.ai (consumer deal distribution). Data flows between all three to create a flywheel effect."
    },
    {
      question: "How does agent reputation work?",
      answer: "Every agent on commerceindex.ai earns a score (0-1000) based on task completion, uptime, error rate, merchant ratings, and security compliance. Higher reputation unlocks more tasks and higher bounties."
    },
    {
      question: "What is USDC escrow?",
      answer: "Agent task payments use USDC stablecoin held in smart contract escrow. Funds release automatically when task completion is verified. This ensures agents get paid and merchants get results."
    },
    {
      question: "How does thedeals.ai pipeline work?",
      answer: "When your Deal Agent creates a promotion, it automatically publishes to thedeals.ai with full attribution. Consumers browse deals, and conversions flow back to your Commerce Index dashboard with full attribution tracking."
    },
  ];

  const msgPlatforms = {
    telegram: { name: 'Telegram', color: '#0088cc' },
    whatsapp: { name: 'WhatsApp', color: '#25D366' },
    slack: { name: 'Slack', color: '#E01E5A' },
    imessage: { name: 'iMessage', color: '#007AFF', badge: 'beta' },
  };

  const msgConversation = [
    { from: 'user', text: '/brief' },
    { from: 'bot', text: "Good morning! Here's your daily brief:\n\n📈 Revenue: $14,230 (+12%)\n📦 89 orders (3 exceptions)\n🤖 5 agents active, 23 decisions\n🏥 Health Score: 847/1000\n\n⚠️ Inventory Agent flagged 2 items approaching reorder point." },
    { from: 'user', text: '/deals create 20% off sneakers 48h' },
    { from: 'bot', text: "✅ Deal created!\n\n🏷️ 20% off All Sneakers\n⏰ 48 hours (ends Mar 19)\n📡 Published to thedeals.ai\n🔗 Shopify discount synced\n\nDeal Agent confidence: 0.91\nEstimated revenue impact: +$2,400" },
  ];

  return (
    <div className="min-h-screen bg-[#0B1120] text-white">
      <SEOHelmet
        title="Agentic Commerce OS"
        description="Commerce Index is an agentic commerce operating system with 43 specialist AI agents in an 84-role org design. Manage supply chain, marketing, finance, and compliance from a text message. Founded 2026. Free to start."
        path="/"
      />
      {/* ═══════════════════ NAV BAR ═══════════════════ */}
      <nav className="sticky top-0 z-50 border-b border-[#22304A] bg-[#0B1120]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
                  <span className="text-[#0B1120] font-bold text-sm">CI</span>
                </div>
                <span className="font-semibold text-white">Commerce Index</span>
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <a href="#agents" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Agents</a>
                <a href="#ecosystem" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Ecosystem</a>
                <a href="#engines" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Engines</a>
                <a href="#pricing" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Pricing</a>
                <Link to="/cortex" className="text-sm text-[#FFD166] hover:text-[#FFE599] transition-colors font-medium">Cortex</Link>
                <Link to="/playbooks" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Playbooks</Link>
                <Link to="/stories" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Stories</Link>
                <a href="#agent-economy" className="text-sm text-[#7A5CFF] hover:text-[#9B85FF] transition-colors">For Agents</a>
                <Link to="/docs" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Docs</Link>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <CurrencyFlag />
              <Link to="/login" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Sign In</Link>
              <button onClick={() => openPhoneModal({ source: 'nav' })} className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg text-sm font-semibold hover:bg-[#3EF1C7]/90 transition-colors">
                Start free
              </button>
            </div>
            <button className="md:hidden text-[#94A3B8]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#22304A] bg-[#0B1120] px-4 py-4 space-y-3">
            <a href="#agents" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Agents</a>
            <a href="#ecosystem" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Ecosystem</a>
            <a href="#engines" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Engines</a>
            <a href="#pricing" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <Link to="/cortex" className="block text-sm text-[#FFD166] font-medium" onClick={() => setMobileMenuOpen(false)}>Cortex</Link>
            <Link to="/stories" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Stories</Link>
            <a href="#agent-economy" className="block text-sm text-[#7A5CFF] hover:text-[#9B85FF]" onClick={() => setMobileMenuOpen(false)}>For Agents</a>
            <Link to="/docs" className="block text-sm text-[#94A3B8]">Docs</Link>
            <Link to="/login" className="block text-sm text-[#94A3B8]">Sign In</Link>
            <button onClick={() => { setMobileMenuOpen(false); openPhoneModal({ source: 'mobile_nav' }); }} className="block w-full text-center px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg text-sm font-semibold">Start free</button>
          </div>
        )}
      </nav>

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative overflow-hidden">
        {/* Gradient mesh background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-50%] left-[-20%] w-[600px] h-[600px] rounded-full bg-[#3EF1C7]/5 blur-[120px]" />
          <div className="absolute top-[-30%] right-[-10%] w-[400px] h-[400px] rounded-full bg-[#7A5CFF]/5 blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* MOBILE OVERFLOW FIX: min-w-0 lets the track shrink below content min-width on small viewports */}
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 mb-6">
                <Bot className="w-4 h-4 text-[#3EF1C7]" />
                <span className="text-sm text-[#3EF1C7] font-medium">Commerce Index · The Agentic Commerce OS</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Find the money your store is{' '}
                <span className="text-[#3EF1C7]">leaking — this week.</span>
              </h1>
              <p className="text-lg text-[#94A3B8] mb-6 max-w-xl">
                AI agents scan your pricing, inventory, and fulfillment for dollars you're losing — then fix it and text you the results. Every Monday: <span className="text-white font-semibold">"Money Found: $___"</span>. Free deal distribution on <a href="https://thedeals.ai" target="_blank" rel="noopener noreferrer" className="text-[#3EF1C7] hover:underline">thedeals.ai</a>, every agent decision auditable on <a href="https://commerceindex.ai" target="_blank" rel="noopener noreferrer" className="text-[#3EF1C7] hover:underline">commerceindex.ai</a>.
              </p>
              {/* Proof bar — live network Money Found counter */}
              <NetworkCounter />
              <div className="flex flex-wrap gap-3 mb-3">
                <a
                  href={`https://wa.me/${WHATSAPP_SIGNUP_NUMBER.replace(/[^\d]/g, '')}?text=${encodeURIComponent('START — find the money my store is leaking')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-[#0B1120] rounded-lg font-semibold hover:bg-[#25D366]/90 transition-colors"
                  onClick={() => { if (window.dataLayer) window.dataLayer.push({ event: 'whatsapp_signup_click', source: 'hero_primary' }); }}
                >
                  <MessageCircle className="w-5 h-5" /> Message us on WhatsApp — that's the signup
                </a>
                <button onClick={() => openPhoneModal({ source: 'hero_secondary' })} className="inline-flex items-center gap-2 px-6 py-3 border border-[#22304A] text-white rounded-lg font-semibold hover:bg-[#1E293B] transition-colors">
                  <Phone className="w-5 h-5" /> Or use your number
                </button>
                <ShopifyAppStoreCTA variant="secondary" label="Install on Shopify" subtext="" />
              </div>
              {/* MOBILE OVERFLOW FIX: allow wrap on narrow viewports */}
              <div className="flex flex-wrap items-center gap-2 mb-6 text-xs text-[#64748B]">
                <span>One message = signed up</span><span>·</span>
                <span>Free to start</span><span>·</span>
                <span>No card, no forms</span><span>·</span>
                <span>Cancel anytime</span>
              </div>
              <div className="flex flex-wrap items-center gap-4 mb-8 text-sm">
                <a href="https://commerceindex.ai/agents" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#7A5CFF] hover:underline">
                  <Bot className="w-4 h-4" /> See agent track record
                </a>
                <Link to="/playbooks" className="inline-flex items-center gap-1.5 text-[#94A3B8] hover:text-white transition-colors">
                  <BarChart3 className="w-4 h-4" /> Read our playbooks
                </Link>
                <a href="#pricing" className="inline-flex items-center gap-1 text-[#94A3B8] hover:text-white transition-colors">
                  Pricing <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              {/* Polsia / SAP / Oracle augmentation banner */}
              <div className="mb-8 p-3 rounded-lg border border-[#7A5CFF]/30 bg-[#7A5CFF]/5 flex items-center gap-3">
                <Network className="w-5 h-5 text-[#7A5CFF] flex-shrink-0" />
                <div className="text-sm text-[#94A3B8]">
                  Already on <span className="text-white font-semibold">Polsia</span>, <span className="text-white font-semibold">SAP</span>, or <span className="text-white font-semibold">Oracle</span>? We augment, not replace. <a href="#augmentation" className="text-[#3EF1C7] hover:underline">See how →</a>
                </div>
              </div>

              {/* Logo strip */}
              {/* MOBILE OVERFLOW FIX: allow wrap on narrow viewports */}
              <div className="flex flex-wrap items-center gap-6 text-[#64748B]">
                <span className="text-xs uppercase tracking-wider">Works with</span>
                <div className="flex flex-wrap items-center gap-4">
                  {['Shopify', 'Telegram', 'Slack', 'WhatsApp', 'thedeals.ai', 'USDC'].map(p => (
                    <span key={p} className="text-xs text-[#94A3B8] px-2 py-1 rounded bg-[#1E293B]">{p}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="hidden lg:block min-w-0">{/* min-w-0: see hero overflow fix above */}
              <AnimatedTerminal />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ ECOSYSTEM FLYWHEEL ═══════════════════ */}
      <section id="ecosystem" className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 mb-4">
              <Globe className="w-4 h-4 text-[#3EF1C7]" />
              <span className="text-sm text-[#3EF1C7] font-medium">Commerce Ecosystem</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Three Platforms. One Flywheel.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { name: 'commerceindex.com', color: '#3EF1C7', subtitle: 'Merchant Operating System', desc: 'AI agents, health scores, messaging-first operations' },
              { name: 'commerceindex.ai', color: '#7A5CFF', subtitle: 'Agent Marketplace & AI', desc: 'Agent registry, task bounties, A2A protocol, reputation' },
              { name: 'thedeals.ai', color: '#FFD166', subtitle: 'Consumer Deal Network', desc: 'Deal distribution, consumer discovery, conversion attribution' },
            ].map((platform, i) => (
              <div key={platform.name} className="p-6 rounded-xl border border-[#22304A] bg-[#0D1628] text-center hover:border-opacity-50 transition-all" style={{ borderColor: platform.color + '40' }}>
                <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: platform.color + '15' }}>
                  <Globe className="w-6 h-6" style={{ color: platform.color }} />
                </div>
                <h3 className="font-semibold text-lg mb-1" style={{ color: platform.color }}>{platform.name}</h3>
                <p className="text-sm text-white font-medium mb-2">{platform.subtitle}</p>
                <p className="text-xs text-[#94A3B8]">{platform.desc}</p>
              </div>
            ))}
          </div>

          {/* Flywheel flow arrows */}
          <div className="flex items-center justify-center gap-4 text-[#64748B]">
            <span className="text-sm font-medium text-[#3EF1C7]">Merchants</span>
            <ArrowRight className="w-5 h-5 text-[#3EF1C7]" />
            <span className="text-sm font-medium text-[#7A5CFF]">Agents</span>
            <ArrowRight className="w-5 h-5 text-[#7A5CFF]" />
            <span className="text-sm font-medium text-[#FFD166]">Consumers</span>
            <ArrowRight className="w-5 h-5 text-[#FFD166]" />
            <span className="text-sm font-medium text-[#3EF1C7]">Data Flywheel</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════ PLATFORM OVERVIEW STRIP ═══════════════════ */}
      <section className="border-y border-[#22304A] bg-[#0D1628]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6">
            {[
              { icon: Bot, label: 'AI Agents', sub: '43 specialist agents · 84-role org design' },
              { icon: MessageSquare, label: 'Messaging Gateway', sub: 'Telegram, Slack, WhatsApp' },
              { icon: Activity, label: 'Health Score', sub: '0-1000 unified score' },
              { icon: Network, label: 'Decision Graph', sub: 'Traces & precedents' },
              { icon: Code2, label: 'API Platform', sub: 'REST + webhooks' },
              { icon: Shield, label: '5-Layer Security', sub: 'Enterprise grade' },
              { icon: Users, label: 'Agent Marketplace', sub: 'Discover & deploy' },
              { icon: Megaphone, label: 'Deal Distribution', sub: 'thedeals.ai pipeline' },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="text-center p-4 rounded-xl border border-[#22304A] bg-[#0D1628] hover:border-[#3EF1C7]/30 transition-colors">
                <Icon className="w-8 h-8 text-[#3EF1C7] mx-auto mb-3" />
                <div className="font-semibold text-sm text-white">{label}</div>
                <div className="text-xs text-[#64748B] mt-1">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ AGENT SHOWCASE ═══════════════════ */}
      <section id="agents" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7A5CFF]/10 border border-[#7A5CFF]/20 mb-4">
              <Bot className="w-4 h-4 text-[#7A5CFF]" />
              <span className="text-sm text-[#7A5CFF] font-medium">AI Agents</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Meet Your Commerce Agents
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              One AI CEO coordinating 43 specialist agents in an 84-role org design. Every agent observes, decides, acts, and learns — and earns autonomy through the Trust Ladder.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-16">
            {agents.map((agent, i) => {
              const Icon = agent.icon;
              const tierColor = trustTiers.find(t => t.name.toLowerCase() === agent.tier)?.color || '#94A3B8';
              return (
                <div key={agent.name} className="p-6 rounded-xl border border-[#22304A] bg-[#0D1628] hover:border-[#3EF1C7]/30 transition-all group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-[#1E293B] group-hover:bg-[#3EF1C7]/10 transition-colors">
                      <Icon className="w-6 h-6 text-[#3EF1C7]" />
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full border" style={{ color: tierColor, borderColor: tierColor + '40' }}>
                      {agent.tier}
                    </span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{agent.name}</h3>
                  <p className="text-sm text-[#94A3B8] mb-4">{agent.desc}</p>
                  <div className="text-xs text-[#64748B]">{agent.decisions} decisions made</div>
                </div>
              );
            })}
          </div>

          {/* Trust Ladder */}
          <div className="p-8 rounded-xl border border-[#22304A] bg-[#0D1628]">
            <h3 className="text-xl font-semibold mb-6 text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              The Trust Ladder
            </h3>
            <p className="text-sm text-[#94A3B8] text-center mb-8 max-w-2xl mx-auto">
              Every agent starts on Probation. As they prove themselves with successful decisions, they automatically earn more autonomy.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {trustTiers.map((tier, i) => (
                <div key={tier.name} className="text-center p-4 rounded-lg border border-[#22304A] bg-[#0B1120]">
                  <div className="w-4 h-4 rounded-full mx-auto mb-3" style={{ backgroundColor: tier.color }} />
                  <div className="font-semibold text-sm mb-1" style={{ color: tier.color }}>{tier.name}</div>
                  <div className="text-xs text-[#94A3B8] mb-2">{tier.desc}</div>
                  <div className="text-xs text-[#64748B]">{tier.threshold}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ MESSAGING-FIRST DEMO ═══════════════════ */}
      <section id="messaging" className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 mb-4">
              <MessageSquare className="w-4 h-4 text-[#3EF1C7]" />
              <span className="text-sm text-[#3EF1C7] font-medium">Messaging-First</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Your Business, In Your Pocket
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Manage everything through Telegram, WhatsApp, or Slack — and iMessage
              in beta via Mac relay. No dashboard required. Texting works too: SMS
              us and you're signed up.
            </p>
          </div>

          {/* Platform tabs */}
          {/* MOBILE OVERFLOW FIX: allow tab row to wrap on narrow viewports */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {Object.entries(msgPlatforms).map(([key, { name, badge }]) => (
              <button key={key}
                onClick={() => setActiveMsgPlatform(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeMsgPlatform === key ? 'bg-[#3EF1C7] text-[#0B1120]' : 'bg-[#1E293B] text-[#94A3B8] hover:text-white'
                }`}
              >
                {name}
                {badge && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide opacity-70">{badge}</span>
                )}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Chat mockup */}
            <div className="rounded-xl border border-[#22304A] bg-[#0B1120] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#22304A] flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#3EF1C7]/20 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[#3EF1C7]" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Commerce Index</div>
                  <div className="text-xs text-[#64748B]">Online — {msgPlatforms[activeMsgPlatform].name}</div>
                </div>
              </div>
              <div className="p-4 space-y-4 min-h-[400px]">
                {msgConversation.map((msg, i) => (
                  <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm whitespace-pre-line ${
                      msg.from === 'user'
                        ? 'bg-[#3EF1C7] text-[#0B1120] rounded-br-md'
                        : 'bg-[#1E293B] text-[#E2E8F0] rounded-bl-md'
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Backend trace */}
            <div className="rounded-xl border border-[#22304A] bg-[#0B1120] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#22304A]">
                <span className="text-sm font-semibold text-[#94A3B8]">Agent Decision Trace</span>
              </div>
              <div className="p-4 font-mono text-xs space-y-3">
                <div className="text-[#64748B]">// Message received from {msgPlatforms[activeMsgPlatform].name}</div>
                <div className="p-3 rounded bg-[#0D1628] border border-[#22304A]">
                  <div className="text-[#3EF1C7]">Intent Classification</div>
                  <div className="text-[#94A3B8] mt-1">intent: "daily_brief" | confidence: 0.97</div>
                  <div className="text-[#94A3B8]">model: ollama/mistral-7b | latency: 120ms</div>
                </div>
                <div className="p-3 rounded bg-[#0D1628] border border-[#22304A]">
                  <div className="text-[#FFD166]">Agent Activation</div>
                  <div className="text-[#94A3B8] mt-1">agent: CX Agent (cx_merchant_abc)</div>
                  <div className="text-[#94A3B8]">trust_tier: supervised | approval: auto</div>
                  <div className="text-[#94A3B8]">guardrails: max_50_actions/hr, $1000 limit</div>
                </div>
                <div className="p-3 rounded bg-[#0D1628] border border-[#22304A]">
                  <div className="text-[#7A5CFF]">Data Aggregation</div>
                  <div className="text-[#94A3B8] mt-1">revenue_query: 340ms | orders_query: 120ms</div>
                  <div className="text-[#94A3B8]">agent_stats: 80ms | health_score: 60ms</div>
                  <div className="text-[#94A3B8]">total_latency: 600ms</div>
                </div>
                <div className="p-3 rounded bg-[#0D1628] border border-[#22304A]">
                  <div className="text-[#3EF1C7]">Decision Recorded</div>
                  <div className="text-[#94A3B8] mt-1">decision_id: dec_a1b2c3d4e5f6</div>
                  <div className="text-[#94A3B8]">action: summarize_daily | status: executed</div>
                  <div className="text-[#94A3B8]">outcome: delivered via {activeMsgPlatform}</div>
                </div>
                <div className="p-3 rounded bg-[#0D1628] border border-[#22304A]">
                  <div className="text-[#3EF1C7]">Deal Creation Flow</div>
                  <div className="text-[#94A3B8] mt-1">agent: Deal Agent | confidence: 0.91</div>
                  <div className="text-[#94A3B8]">actions: create_deal → publish_thedeals → sync_shopify</div>
                  <div className="text-[#94A3B8]">credits_used: 25 (deep operation)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Command reference preview */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {['/pulse', '/brief', '/deals', '/margins', '/agents', '/goals', '/inventory', '/report', '/digest', '/policy', '/recall', '/threads'].map(cmd => (
              <div key={cmd} className="px-3 py-2 rounded-lg bg-[#1E293B] border border-[#22304A] text-center">
                <span className="text-xs font-mono text-[#3EF1C7]">{cmd}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/docs/commands" className="text-sm text-[#3EF1C7] hover:underline inline-flex items-center gap-1">
              View all 30+ commands <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ COMMERCE ENGINES ═══════════════════ */}
      <section id="engines" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/20 mb-4">
              <Layers className="w-4 h-4 text-[#FFD166]" />
              <span className="text-sm text-[#FFD166] font-medium">Commerce Engines</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              50+ engines across 9 capability groups
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Your full back office, in code. Four flagship product engines on top of a 9-group foundation stack.
            </p>
          </div>

          {/* Flagship product engines */}
          <div className="mb-4 text-xs uppercase tracking-wider text-[#64748B] text-center">Flagship product engines</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {flagshipEngines.map(engine => {
              const Icon = engine.icon;
              return (
                <div key={engine.name} className="p-6 rounded-xl border border-[#22304A] bg-[#0D1628] hover:border-opacity-50 transition-all">
                  <div className="p-3 rounded-lg bg-[#1E293B] w-fit mb-4">
                    <Icon className="w-6 h-6" style={{ color: engine.color }} />
                  </div>
                  <h3 className="font-semibold text-lg mb-4" style={{ color: engine.color }}>{engine.name}</h3>
                  <ul className="space-y-2 mb-4">
                    {engine.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[#94A3B8]">
                        <Check className="w-3 h-3 text-[#3EF1C7] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => openPhoneModal({ source: 'flagship_engine', intended_feature: engine.name.toLowerCase().replace(/ /g, '_') })}
                    className="text-xs font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                    style={{ color: engine.color }}
                  >
                    Try {engine.name.replace(' Engine', '')} <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Foundation capability groups */}
          <div className="mb-4 text-xs uppercase tracking-wider text-[#64748B] text-center">Foundation engines · 9 capability groups</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {foundationEngines.map(group => {
              const Icon = group.icon;
              return (
                <Link to={`/engines#${group.slug}`} key={group.slug} className="p-5 rounded-xl border border-[#22304A] bg-[#0D1628] hover:border-[#3EF1C7]/40 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-[#1E293B]">
                      <Icon className="w-5 h-5" style={{ color: group.color }} />
                    </div>
                    <h3 className="font-semibold" style={{ color: group.color }}>{group.name}</h3>
                  </div>
                  <ul className="space-y-1 text-xs text-[#94A3B8] mb-3">
                    {group.subEngines.slice(0, 4).map(s => (
                      <li key={s} className="truncate">· {s}</li>
                    ))}
                    {group.subEngines.length > 4 && (
                      <li className="text-[#64748B]">· +{group.subEngines.length - 4} more</li>
                    )}
                  </ul>
                  <div className="text-xs text-[#3EF1C7] inline-flex items-center gap-1">See all → </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/engines" className="inline-flex items-center gap-2 px-6 py-3 border border-[#22304A] text-white rounded-lg font-semibold text-sm hover:bg-[#11192A] transition-colors">
              <Layers className="w-4 h-4" /> Browse the full engines catalog
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ AGENT ECONOMY ═══════════════════ */}
      <section id="agent-economy" className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7A5CFF]/10 border border-[#7A5CFF]/20 mb-4">
              <Bot className="w-4 h-4 text-[#7A5CFF]" />
              <span className="text-sm text-[#7A5CFF] font-medium">Agent Economy</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              The Agent Economy
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Build, deploy, and monetize AI agents on commerceindex.ai
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Agent Score Card */}
            <div className="p-6 rounded-xl border border-[#7A5CFF]/30 bg-[#0D1628]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg">Agent Score Card</h3>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[#7A5CFF]">782</div>
                  <div className="text-xs text-[#64748B]">/1000</div>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Task Completion', value: 92, display: '92%' },
                  { label: 'Uptime', value: 99.1, display: '99.1%' },
                  { label: 'Error Rate', value: 2.1, display: '2.1%', inverse: true },
                  { label: 'Merchant Rating', value: 94, display: '4.7/5' },
                  { label: 'Security Score', value: 95, display: '95%' },
                  { label: 'Response Time', value: 66, display: '340ms' },
                ].map(metric => (
                  <div key={metric.label}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-[#94A3B8]">{metric.label}</span>
                      <span className="text-sm font-medium text-white">{metric.display}</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[#1E293B]">
                      <div className="h-2 rounded-full bg-[#7A5CFF] transition-all" style={{ width: `${metric.inverse ? 100 - metric.value : metric.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Feature tiles */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Zap, title: 'Task Bounties', desc: 'Agents claim tasks, earn USDC', color: '#FFD166' },
                { icon: Wallet, title: 'USDC Escrow', desc: 'Smart contract payment guarantees', color: '#3EF1C7' },
                { icon: Network, title: 'A2A Protocol', desc: 'Agent-to-agent communication', color: '#7A5CFF' },
                { icon: Star, title: 'Reputation Tiers', desc: 'Bronze → Silver → Gold → Platinum', color: '#F55454' },
              ].map(feature => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="p-5 rounded-xl border border-[#22304A] bg-[#0D1628] hover:border-[#7A5CFF]/30 transition-colors">
                    <div className="p-3 rounded-lg bg-[#1E293B] w-fit mb-3">
                      <Icon className="w-6 h-6" style={{ color: feature.color }} />
                    </div>
                    <h4 className="font-semibold text-sm mb-1">{feature.title}</h4>
                    <p className="text-xs text-[#94A3B8]">{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center mt-10">
            <a href="https://commerceindex.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#7A5CFF] text-white rounded-lg font-semibold hover:bg-[#7A5CFF]/90 transition-colors">
              Register your agent on commerceindex.ai <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════ COMMERCE HEALTH SCORE ═══════════════════ */}
      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 mb-4">
              <Activity className="w-4 h-4 text-[#3EF1C7]" />
              <span className="text-sm text-[#3EF1C7] font-medium">Commerce Health</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              One Score. Complete Picture.
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Your Commerce Health Score (0-1000) unifies everything into a single actionable metric.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                name: 'CI Score', weight: '40%', score: 78, color: '#3EF1C7',
                details: 'Price (35%) + Inventory (10%) + Trend (15%) + Competitor (20%) + Discount (10%) + Reliability (10%)'
              },
              {
                name: 'Margin Guard', weight: '30%', score: 92, color: '#7A5CFF',
                details: 'Promise Integrity Score + Post-Checkout Risk + Backorder Risk + Break Drivers'
              },
              {
                name: 'Decision Graph', weight: '30%', score: 81, color: '#FFD166',
                details: 'Agent decisions + Precedent learning + Policy compliance + What-If accuracy'
              },
            ].map(s => (
              <div key={s.name} className="p-6 rounded-xl border border-[#22304A] bg-[#0D1628] text-center">
                <div className="text-3xl font-bold mb-1" style={{ color: s.color }}>{s.score}</div>
                <div className="text-xs text-[#64748B] mb-3">/100</div>
                <div className="font-semibold mb-1">{s.name}</div>
                <div className="text-xs text-[#3EF1C7] mb-3">{s.weight} of total</div>
                <div className="text-xs text-[#94A3B8]">{s.details}</div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <div className="inline-flex items-center gap-4 p-4 rounded-xl border border-[#22304A] bg-[#0B1120]">
              <span className="text-[#64748B] text-sm">Combined Health Score:</span>
              <span className="text-4xl font-bold text-[#3EF1C7]">847</span>
              <span className="text-[#64748B] text-lg">/1000</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ DECISION GRAPH ═══════════════════ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Every Decision Has a Paper Trail
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              The Decision Graph records every agent action, learns from outcomes, and applies precedents to future decisions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: 'Decision Traces', desc: 'Full audit trail of every agent decision — signal, reasoning, confidence, and outcome.' },
              { icon: Brain, title: 'Precedent Learning', desc: 'Agents learn from past decisions. Similar situations trigger proven playbooks.' },
              { icon: Target, title: 'What-If Simulator', desc: 'Test scenarios before committing. See projected outcomes before agents act.' },
              { icon: Shield, title: 'Policy Gates', desc: 'Define rules that agents must follow. Block actions that violate business policies.' },
            ].map(item => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="p-6 rounded-xl border border-[#22304A] bg-[#0D1628]">
                  <Icon className="w-8 h-8 text-[#3EF1C7] mb-4" />
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-[#94A3B8]">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════ INTEGRATIONS ═══════════════════ */}
      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Connects to Everything You Use
            </h2>
            <Link to="/integrations" className="inline-flex items-center gap-1 text-sm text-[#3EF1C7] hover:underline">
              See all integrations — LLMs, agents, MCP, channels <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
            {[
              { category: 'Commerce', items: ['Shopify', 'WooCommerce', 'BigCommerce'] },
              { category: 'Messaging', items: ['Telegram', 'Slack', 'WhatsApp', 'iMessage'] },
              { category: 'Payments', items: ['Stripe', 'USDC'] },
              { category: 'AI & Compute', items: ['Claude AI', 'Ollama'] },
              { category: 'Infrastructure', items: ['Cloudflare', 'Zapier', 'Chrome'] },
              { category: 'Ecosystem', items: ['thedeals.ai', 'commerceindex.ai'] },
            ].map(group => (
              <div key={group.category}>
                <h4 className="text-xs uppercase tracking-wider text-[#64748B] mb-3 font-semibold">{group.category}</h4>
                <div className="space-y-2">
                  {group.items.map(name => (
                    <div key={name} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#0D1628] text-sm text-[#94A3B8] hover:text-white hover:border-[#3EF1C7]/30 transition-colors">
                      {name}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ THREE PATHS ═══════════════════ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Four Ways to Start
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Phone, title: 'Phone signup', desc: 'Text us your number and get started in 60 seconds. WhatsApp or SMS — no email, no password, no form.', cta: 'Start with phone', link: '__OPEN_PHONE_MODAL__', color: '#3EF1C7', external: false },
              { icon: Store, title: 'Already on Shopify?', desc: 'Install from the App Store. Auto-detects your store — no URL entry. Sync products, inventory, orders.', cta: 'Install from App Store', link: LINKS.shopifyInstallUrl, color: '#3EF1C7', external: true },
              { icon: Code2, title: 'Any Platform', desc: 'REST API + webhooks. Connect any e-commerce platform. Full API access to all agents, engines, and intelligence.', cta: 'Get API Key', link: '/docs/integrations', color: '#7A5CFF', external: false },
              { icon: Bot, title: 'Agent Developer', desc: 'Build AI agents on commerceindex.ai. Claim task bounties, earn reputation, get paid in USDC. Full A2A protocol support.', cta: 'Build Agents', link: 'https://commerceindex.ai', color: '#7A5CFF', external: true },
            ].map(path => {
              const Icon = path.icon;
              return (
                <div key={path.title} className="p-8 rounded-xl border border-[#22304A] bg-[#0D1628] text-center">
                  <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: path.color + '15' }}>
                    <Icon className="w-8 h-8" style={{ color: path.color }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{path.title}</h3>
                  <p className="text-sm text-[#94A3B8] mb-6">{path.desc}</p>
                  {path.link === '__OPEN_PHONE_MODAL__' ? (
                    <button onClick={() => openPhoneModal({ source: 'paths_card', persona: path.title })} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-colors" style={{ backgroundColor: path.color, color: '#0B1120' }}>
                      {path.cta} <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : path.external ? (
                    <a href={path.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-colors" style={{ backgroundColor: path.color, color: '#0B1120' }}>
                      {path.cta} <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link to={path.link} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-colors" style={{ backgroundColor: path.color, color: '#0B1120' }}>
                      {path.cta} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════ SECURITY ═══════════════════ */}
      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Enterprise-Grade Security
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              5-layer defense system protecting every request, every agent, every decision.
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { num: '01', title: 'Request Validation', desc: 'Input sanitization, schema validation, HMAC verification' },
              { num: '02', title: 'Agent Identity', desc: 'Cryptographic agent signatures, session tokens, scoped API keys' },
              { num: '03', title: 'Rate Limiting', desc: 'Per-agent, per-merchant, per-endpoint rate controls' },
              { num: '04', title: 'Behavioral Analysis', desc: 'Anomaly detection, pattern matching, drift monitoring' },
              { num: '05', title: 'VPS Isolation', desc: 'Sandboxed execution, network segmentation, audit logging' },
            ].map(layer => (
              <div key={layer.num} className="p-4 rounded-xl border border-[#22304A] bg-[#0B1120]">
                <div className="text-2xl font-bold text-[#3EF1C7] mb-2">{layer.num}</div>
                <div className="font-semibold text-sm mb-2">{layer.title}</div>
                <div className="text-xs text-[#94A3B8]">{layer.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PRICING ═══════════════════ */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Plans That Pay for Themselves
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto mb-3">
              Every plan comes with the weekly <span className="text-white font-medium">Money Found</span> statement — the dollars our agents found you, in writing. If it doesn't beat your subscription, you'll see that too.
            </p>
            <p className="text-xs text-[#64748B] max-w-2xl mx-auto mb-8">
              14-day free trial on paid plans · No card required to start free · Cancel anytime
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-3 p-1 rounded-lg bg-[#1E293B]">
              <button onClick={() => setBillingCycle('monthly')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${billingCycle === 'monthly' ? 'bg-[#3EF1C7] text-[#0B1120]' : 'text-[#94A3B8]'}`}>
                Monthly
              </button>
              <button onClick={() => setBillingCycle('annual')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${billingCycle === 'annual' ? 'bg-[#3EF1C7] text-[#0B1120]' : 'text-[#94A3B8]'}`}>
                Annual
              </button>
            </div>
          </div>

          {/* Merchant platform tiers (Axis 1 — the primary offer, Stripe-billed) */}
          <div id="platform-tiers" data-testid="approved-tiers" className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { name: 'Free', key: 'free', monthly: '$0', annual: '$0', unit: '' },
              { name: 'Pro', key: 'pro', monthly: '$29', annual: '$276', equiv: '$23', highlight: true },
              { name: 'Intelligence Suite', key: 'intelligence_suite', monthly: '$99', annual: '$948', equiv: '$79' },
            ].map(t => (
              <div key={t.key} data-testid={`tier-${t.key}`} className={`p-6 rounded-xl border ${t.highlight ? 'border-[#3EF1C7]' : 'border-[#22304A]'} bg-[#0D1628]`}>
                <h3 className="text-xl font-semibold mb-3">{t.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold" data-testid={`price-${t.key}`}>{billingCycle === 'annual' ? t.annual : t.monthly}</span>
                  {t.key !== 'free' && <span className="text-[#64748B] text-sm">{billingCycle === 'annual' ? '/yr' : '/mo'}</span>}
                </div>
                {t.key !== 'free' && billingCycle === 'annual' && (
                  <p className="text-xs text-[#64748B] mb-4">{t.equiv}/mo billed yearly</p>
                )}
                {/* PAID_TIER_SIGNUP_READY: paid tiers have no billing path yet (no checkout, no Shopify plan handles), so a trial cannot start.
                    Reason: a visible "Start 14-day free trial" that cannot start a trial is untrue. Restore check: set true only after
                    paid-tier billing exists end to end and /merchant/login (or its replacement) is verified to carry plan+billing. */}
                {t.key === 'free' ? (
                  <button
                    onClick={() => openPhoneModal({ source: 'pricing', intended_plan: 'free' })}
                    className="w-full py-3 rounded-lg font-semibold text-sm bg-[#1E293B] text-white"
                  >
                    Start free
                  </button>
                ) : (
                  <button
                    disabled
                    data-testid={`cta-unavailable-${t.key}`}
                    className="w-full py-3 rounded-lg font-semibold text-sm bg-[#1E293B] text-[#64748B] cursor-not-allowed"
                  >
                    Not yet available
                  </button>
                )}
              </div>
            ))}
          </div>
          {/* LEGACY merchant tiers (Free/Starter $79/Pro $299/Scale $999) disabled from display.
              Reason: approved merchant pricing is Free / Pro $29 mo ($276 yr) / Intelligence Suite $99 mo ($948 yr), 14-day trials.
              Restore check: only if the owner re-approves these tiers; the /checkout PLANS and backend subscription_access.py still carry the legacy values.
          <div id="platform-tiers" className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Free', plan: 'free', price: 0, blurb: 'See what you\'re leaking', features: ['CI health score', 'Money Found weekly statement', '5 deals/mo on thedeals.ai', '30 WhatsApp/SMS msgs/mo included'], cta: 'Start free', highlight: false },
              { name: 'Starter', plan: 'starter', price: 79, blurb: 'Agents start fixing it', features: ['Unlimited products & deals', 'Unlimited thedeals.ai publishing', 'AI recommendations & exception radar', '500 msgs/mo included'], cta: 'Start 14-day free trial', highlight: true },
              { name: 'Pro', plan: 'pro', price: 299, blurb: 'Agents run it, you approve', features: ['Everything in Starter', 'Slack approvals & approval queue', 'Decision traces & precedent library', '2,000 msgs/mo included'], cta: 'Start 14-day free trial', highlight: false },
              { name: 'Scale', plan: 'scale', price: 999, blurb: 'Full autonomous operations', features: ['Everything in Pro', 'Signals API & team workflows', 'Funding pack & USDC payouts', '10,000 msgs/mo included'], cta: 'Start 14-day free trial', highlight: false },
            ].map(t => (
              <div key={t.name} className={`p-6 rounded-xl border ${t.highlight ? 'border-[#3EF1C7] ring-1 ring-[#3EF1C7]/20' : 'border-[#22304A]'} bg-[#0D1628] relative`}>
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#3EF1C7] text-[#0B1120] text-xs font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-semibold mb-1">{t.name}</h3>
                <p className="text-xs text-[#64748B] mb-2">{t.blurb}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{fmtPrice(getPrice(t.price))}</span>
                  <span className="text-[#64748B] text-sm">/mo</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {t.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                      <Check className="w-4 h-4 text-[#3EF1C7] mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => t.price === 0
                    ? openPhoneModal({ source: 'pricing', intended_plan: 'free' })
                    : navigate(`/checkout?plan=${t.plan}&billing=${billingCycle}`)}
                  className={`w-full py-3 rounded-lg font-semibold text-sm transition-colors ${
                    t.highlight
                      ? 'bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90'
                      : 'bg-[#1E293B] text-white hover:bg-[#22304A]'
                  }`}>
                  {t.cta}
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#64748B] text-center mt-4">
            Enterprise (SAP/Oracle adapters, SSO, custom cohorts) — <a href="/contact" className="text-[#3EF1C7] hover:underline">contact us</a>.
          </p>
          */}

          {/* API & Agent credit track (Axis 2 — for developers and agents) */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                For Developers &amp; Agents: API Credit Track
              </h3>
              <p className="text-sm text-[#94A3B8]">
                Usage-priced access to the commerce API and MCP tools. 1 credit = $0.01. Separate from the merchant plans above.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {pricingPlans.map(plan => (
                <div key={plan.name} className="p-5 rounded-xl border border-[#22304A] bg-[#0D1628]">
                  <h4 className="font-semibold mb-1">{plan.name}</h4>
                  <div className="mb-1">
                    {plan.price !== null ? (
                      <>
                        <span className="text-2xl font-bold">{fmtPrice(getPrice(plan.price))}</span>
                        <span className="text-[#64748B] text-sm">/mo</span>
                      </>
                    ) : (
                      <span className="text-xl font-bold">Custom</span>
                    )}
                  </div>
                  <div className="text-xs text-[#3EF1C7] mb-3">{plan.credits} credits/mo</div>
                  <button onClick={() => plan.price !== null ? openPhoneModal({ source: 'pricing_api', intended_plan: `api_${plan.name.toLowerCase().replace(/ /g, '_')}` }) : navigate('/contact')}
                    className="w-full py-2 rounded-lg font-semibold text-xs bg-[#1E293B] text-white hover:bg-[#22304A] transition-colors">
                    {plan.cta}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Outcome fee explainer */}
          <div className="mt-12 p-6 rounded-xl border border-[#22304A] bg-[#0D1628] max-w-3xl mx-auto">
            <h3 className="font-semibold text-center mb-4">How Outcome Fees Work</h3>
            <p className="text-sm text-[#94A3B8] text-center mb-6">
              When our agents create measurable commerce value, a small fee applies. You only pay for results.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { action: 'Agent Purchase', fee: '2%' },
                { action: 'Price Optimization', fee: '10% of uplift' },
                { action: 'Cart Recovery', fee: '5%' },
                { action: 'Cross-Sell', fee: '3%' },
              ].map(o => (
                <div key={o.action} className="text-center p-3 rounded-lg bg-[#0B1120]">
                  <div className="text-xs text-[#94A3B8] mb-1">{o.action}</div>
                  <div className="text-sm font-semibold text-[#3EF1C7]">{o.fee}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#64748B] text-center mt-4">
              Higher plans get reduced outcome fees. Enterprise: 1% | Scale: 1.5% | Pro: 2% | Starter: 2% | Free: 0%. Messaging beyond your plan allowance bills at carrier cost + a small volume-tiered fee.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      {/* ═══════════════════ CORTEX TEASER ═══════════════════ */}
      <section className="py-24 relative overflow-hidden border-y border-[#22304A]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-30%] left-[20%] w-[500px] h-[500px] rounded-full bg-[#FFD166]/5 blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[10%] w-[400px] h-[400px] rounded-full bg-[#7A5CFF]/5 blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/20 text-[#FFD166] text-xs font-medium mb-6">
                <Zap className="w-3 h-3" /> Coming Q1 2027
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Introducing<br /><span className="text-[#FFD166]">Cortex</span>
              </h2>
              <p className="text-xl text-[#94A3B8] mb-4">
                The first device that pays for itself.
              </p>
              <p className="text-[#64748B] mb-8 leading-relaxed">
                A silent, fanless appliance that runs your personal AI agent team at home — and sells
                excess compute to the Commerce Index network while you sleep. Like solar panels for intelligence.
              </p>
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#3EF1C7]">$149</div>
                  <div className="text-xs text-[#64748B] mt-1">Starting price</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#3EF1C7]">~$235</div>
                  <div className="text-xs text-[#64748B] mt-1">Yearly earnings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#3EF1C7]">0 dB</div>
                  <div className="text-xs text-[#64748B] mt-1">Silent operation</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/cortex" className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFD166] text-[#0B1120] rounded-lg font-semibold hover:bg-[#FFD166]/90 transition-colors">
                  <Cpu className="w-4 h-4" /> Pre-order Cortex
                </Link>
                <Link to="/cortex" className="inline-flex items-center gap-2 px-6 py-3 border border-[#334155] text-[#94A3B8] rounded-lg hover:text-white hover:border-[#FFD166]/30 transition-colors">
                  Learn more <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#FFD166]/10 blur-[60px] rounded-full" />
                <div className="relative bg-[#0D1628] border border-[#22304A] rounded-2xl p-8 w-72">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3EF1C7] animate-pulse" />
                      <span className="text-xs text-[#64748B] font-mono">CORTEX</span>
                    </div>
                    <span className="text-xs text-[#3EF1C7] font-mono">ONLINE</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-[#0B1120] rounded-lg p-3 border border-[#22304A] flex flex-col items-center gap-2">
                      <Cpu className="w-6 h-6 text-[#3EF1C7]" />
                      <span className="text-[10px] text-[#64748B]">6 TOPS NPU</span>
                    </div>
                    <div className="bg-[#0B1120] rounded-lg p-3 border border-[#22304A] flex flex-col items-center gap-2">
                      <Wifi className="w-6 h-6 text-[#7A5CFF]" />
                      <span className="text-[10px] text-[#64748B]">WiFi 6</span>
                    </div>
                    <div className="bg-[#0B1120] rounded-lg p-3 border border-[#22304A] flex flex-col items-center gap-2">
                      <Shield className="w-6 h-6 text-[#FFD166]" />
                      <span className="text-[10px] text-[#64748B]">ed25519</span>
                    </div>
                    <div className="bg-[#0B1120] rounded-lg p-3 border border-[#22304A] flex flex-col items-center gap-2">
                      <BatteryCharging className="w-6 h-6 text-[#3EF1C7]" />
                      <span className="text-[10px] text-[#64748B]">27W USB-C</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-[#64748B]">Mode</span>
                      <span className="text-[#3EF1C7] font-mono">HYBRID</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#64748B]">Agents</span>
                      <span className="text-white font-mono">8 active</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#64748B]">Grid jobs</span>
                      <span className="text-[#7A5CFF] font-mono">12 today</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#64748B]">Earned</span>
                      <span className="text-[#FFD166] font-mono">$0.64 today</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <div key={i} className="rounded-xl border border-[#22304A] bg-[#0D1628] overflow-hidden">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-medium text-sm">{item.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#64748B] transition-transform ${openFaqIndex === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaqIndex === i && (
                  <div className="px-5 pb-5 text-sm text-[#94A3B8] border-t border-[#22304A] pt-4">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FINAL CTA ═══════════════════ */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute bottom-[-50%] left-[30%] w-[500px] h-[500px] rounded-full bg-[#3EF1C7]/5 blur-[120px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            How Much Is Your Store Leaking Right Now?
          </h2>
          <p className="text-[#94A3B8] mb-8">
            Find out free in minutes. Then get a <span className="text-white font-semibold">"Money Found: $___"</span> text every Monday from agents working your pricing, inventory, and deals — while you focus on growth.
          </p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-6">
            <button onClick={() => openPhoneModal({ source: 'final_cta' })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold hover:bg-[#3EF1C7]/90 transition-colors text-lg">
              <Phone className="w-5 h-5" /> Find my money — free
            </button>
            <span className="text-[#64748B] text-sm">or</span>
            <QRCodeStart whatsappNumber={WHATSAPP_SIGNUP_NUMBER} size={140} label="Scan to start on WhatsApp" />
          </div>
          <p className="text-xs text-[#64748B] mb-6">Free to start · 14-day Pro trial · No card required · Cancel anytime</p>
          <div className="flex justify-center mb-4">
            <ShopifyAppStoreCTA variant="secondary" label="Already on Shopify? Install from App Store" subtext="Auto-detects your store. No URL entry." />
          </div>
          <p className="text-[#94A3B8] mt-6">Building AI agents?</p>
          <a href="https://commerceindex.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 mt-3 bg-[#7A5CFF] text-white rounded-lg font-semibold hover:bg-[#7A5CFF]/90 transition-colors">
            <Bot className="w-5 h-5" /> Register on commerceindex.ai
          </a>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="border-t border-[#22304A] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
                  <span className="text-[#0B1120] font-bold text-sm">CI</span>
                </div>
                <span className="font-semibold">Commerce Index</span>
              </div>
              <p className="text-xs text-[#64748B]">Agentic Commerce OS. Run your business from a text message.</p>
            </div>
            {[
              { title: 'Products', links: [['AI Agents', '/docs/agents'], ['Cortex Hardware', '/cortex'], ['Agent Onboarding', '/agent-onboarding'], ['OpenClaw', '/docs/openclaw'], ['Health Score', '/docs/promise-health'], ['thedeals.ai', 'https://thedeals.ai'], ['CI Proof', '/proof']] },
              { title: 'Platform', links: [['API Reference', '/docs/api-reference'], ['Shopify', '/docs/shopify-setup'], ['Billing', '/billing'], ['Security', '/docs/guardrails']] },
              { title: 'Docs', links: [['Getting Started', '/docs/getting-started'], ['Shopify Guide', '/docs/shopify-setup'], ['OpenClaw Setup', '/docs/channel-setup'], ['Mac Mini', '/docs/mac-mini']] },
              { title: 'Company', links: [['About', '/about'], ['Stories', '/stories'], ['Integrations', '/integrations'], ['Contact', '/contact'], ['Privacy', '/privacy'], ['Terms', '/terms']] },
              { title: 'Ecosystem', links: [['commerceindex.com', 'https://commerceindex.com'], ['commerceindex.ai', 'https://commerceindex.ai'], ['thedeals.ai', 'https://thedeals.ai'], ['Agent Registry', 'https://commerceindex.ai'], ['Task Bounties', 'https://commerceindex.ai']] },
            ].map(col => (
              <div key={col.title}>
                <h4 className="font-semibold text-sm mb-4">{col.title}</h4>
                <ul className="space-y-2">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      {href.startsWith('http') ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs text-[#64748B] hover:text-[#3EF1C7] transition-colors">{label}</a>
                      ) : (
                        <Link to={href} className="text-xs text-[#64748B] hover:text-[#3EF1C7] transition-colors">{label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-[#22304A] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#64748B]">&copy; {new Date().getFullYear()} Commerce Index. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-xs text-[#64748B] hover:text-white">Privacy</Link>
              <Link to="/terms" className="text-xs text-[#64748B] hover:text-white">Terms</Link>
              <Link to="/contact" className="text-xs text-[#64748B] hover:text-white">Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      <PhoneStartModal
        open={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        intendedPlan={phoneModalContext.intended_plan}
        intendedFeature={phoneModalContext.intended_feature}
      />
    </div>
  );
                }
